'use client';

import { c as _c } from "react/compiler-runtime";
import type { Command } from '@/commands/types';
import type { MergeMethod } from '@/lib/pull-requests/types';
import { GitMerge } from 'lucide-react';
import { useMemo } from 'react';
import { useEventCallback } from 'usehooks-ts';
import type { PrimaryButtonConfigInput, PrimaryMergeAction, PrV2MergeChoice } from './pr-v2-merge-choices';
import { getPrV2MergeChoices } from './pr-v2-merge-choices';
import { confirmCommandAction } from '@/commands/confirm-command-action';
import { prV2Command, prV2ModeRow } from '@/commands/modules/pr-v2-command-group';
import { useRegisterCommands } from '@/commands/use-commands';

/**
 * Registers "Merge..." and one row per action the merge button could run, while the pull request is
 * open and no merge is running. `buttonInput` is null until the mergeability status loads.
 */
export function usePrV2MergeCommands(t0) {
  const $ = _c(13);
  const {
    buttonInput,
    isPrOpen,
    number,
    selectableMergeMethods,
    selectMergeMethod,
    runMergeAction
  } = t0;
  let t1;
  if ($[0] !== buttonInput || $[1] !== number || $[2] !== selectableMergeMethods) {
    t1 = buttonInput ? getPrV2MergeChoices({
      ...buttonInput,
      number,
      selectableMergeMethods
    }) : [];
    $[0] = buttonInput;
    $[1] = number;
    $[2] = selectableMergeMethods;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const choices = t1;
  let t2;
  if ($[4] !== runMergeAction || $[5] !== selectMergeMethod) {
    t2 = choice => {
      const run = () => {
        if (choice.method) {
          selectMergeMethod(choice.method);
        }
        runMergeAction(choice.action);
      };
      return choice.confirmMessage ? confirmCommandAction(choice.confirmMessage, run) : run();
    };
    $[4] = runMergeAction;
    $[5] = selectMergeMethod;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const runChoice = useEventCallback(t2);
  let t3;
  let t4;
  if ($[7] !== buttonInput || $[8] !== choices || $[9] !== isPrOpen || $[10] !== runChoice) {
    t3 = () => {
      if (!buttonInput || !isPrOpen || buttonInput.isPending) {
        return [];
      }
      const rows = choices.length > 0 ? choices.map(choice_0 => prV2ModeRow({
        id: `pr-v2-merge:${choice_0.id}`,
        label: choice_0.label,
        tag: "pr-v2-merge",
        keywords: choice_0.method ? ["merge", choice_0.method] : ["merge"],
        checked: choice_0.isPreferred,
        action: () => runChoice(choice_0)
      })) : [prV2ModeRow({
        id: "pr-v2-merge-empty",
        label: buttonInput.status.humanReadableMergeStatus,
        tag: "pr-v2-merge-empty",
        action: _temp
      })];
      return [prV2Command("pr-v2-merge", {
        label: "Merge...",
        icon: <GitMerge />,
        shortcutId: "pr-merge-open-menu",
        enterMode: "pr-v2-merge",
        action: _temp2
      }), ...rows];
    };
    t4 = [buttonInput, choices, isPrOpen, runChoice];
    $[7] = buttonInput;
    $[8] = choices;
    $[9] = isPrOpen;
    $[10] = runChoice;
    $[11] = t3;
    $[12] = t4;
  } else {
    t3 = $[11];
    t4 = $[12];
  }
  useRegisterCommands(t3, t4);
}
function _temp2() {
  return false;
}
function _temp() {}